/**
 * ============================================================
 * QUANTITY SWATCHES — Alivia Wellness
 * ============================================================
 * Handles click/keyboard events on the quantity swatch buttons,
 * updates the hidden quantity input, recalculates displayed
 * prices, and integrates with Seal Subscriptions.
 *
 * Upload to: Assets/quantity-swatches.js
 * ============================================================
 */

(function () {
  'use strict';

  /* ── Selectors ── */
  const SWATCH_CONTAINER = '#quantity-swatches';
  const SWATCH_ITEM      = '.js-qs-item';
  const QTY_INPUT        = 'input[name="quantity"]';
  const SELLING_PLAN     = 'input[name="selling_plan"]';
  const SUBS_RADIO       = 'input[name^="subs_type_"]';
  const ACTIVE_CLASS     = 'is-active';

  /* ── Helpers ── */
  function formatMoney(cents, currency) {
    const amount = (cents / 100).toFixed(2);
    const symbols = { AUD: 'A$', USD: '$', GBP: '£', EUR: '€', NZD: 'NZ$', CAD: 'C$' };
    const symbol = symbols[currency] || '$';
    return symbol + amount;
  }

  /* ── Init ── */
  function init() {
    const container = document.querySelector(SWATCH_CONTAINER);
    if (!container) return;

    const form         = container.closest('form') || document.querySelector('form[action*="/cart/add"]');
    const qtyInput     = form ? form.querySelector(QTY_INPUT) : document.querySelector(QTY_INPUT);
    const items        = container.querySelectorAll(SWATCH_ITEM);
    const unitPrice    = parseInt(container.dataset.unitPrice, 10);
    const comparePrice = parseInt(container.dataset.comparePrice, 10);
    const perPack      = parseInt(container.dataset.itemsPerPack, 10);
    const currency     = container.dataset.currency || 'AUD';

    if (!items.length) return;

    /* ── Hide the default Prestige quantity stepper ── */
    const quantityStepper = form
      ? form.querySelector('.quantity-selector, .product-info__quantity, [class*="quantity-picker"]')
      : null;
    if (quantityStepper) {
      quantityStepper.style.display = 'none';
    }

    /* ── Also hide the default product price block (we show our own) ── */
    // Uncomment the next lines if you want the swatches to OWN the price display:
    // const defaultPrice = document.querySelector('.product-info__block-item .price');
    // if (defaultPrice) defaultPrice.closest('.product-info__block-item').style.display = 'none';

    /* ── Show the swatch container (hidden by default to avoid FOUC) ── */
    container.style.display = '';

    /* ── Swatch click handler ── */
    function selectSwatch(swatch) {
      /* Toggle active states */
      items.forEach(function (item) {
        item.classList.remove(ACTIVE_CLASS);
        item.setAttribute('aria-checked', 'false');
      });
      swatch.classList.add(ACTIVE_CLASS);
      swatch.setAttribute('aria-checked', 'true');

      /* Update hidden quantity input */
      var qty = parseInt(swatch.dataset.quantity, 10);
      if (qtyInput) {
        qtyInput.value = qty;
        /* Dispatch change event so other scripts (Seal, etc.) can react */
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /* Update the main price display on the page */
      updatePriceDisplay(swatch, currency);

      /* Persist selection */
      try {
        localStorage.setItem('qs-selected-qty', qty);
      } catch (e) { /* silent */ }
    }

    /* ── Price display updater ── */
    function updatePriceDisplay(swatch, currency) {
      var saleTotal  = parseInt(swatch.dataset.saleTotal, 10);
      var rawTotal   = parseInt(swatch.dataset.rawTotal, 10);
      var perItem    = parseInt(swatch.dataset.perItem, 10);
      var discount   = parseInt(swatch.dataset.discount, 10);
      var totalItems = parseInt(swatch.dataset.totalItems, 10);

      /* ── Check if subscription is active and adjust ── */
      var subDiscount = getSubscriptionDiscount();
      if (subDiscount > 0) {
        var subAmount = saleTotal * subDiscount / 100;
        saleTotal = Math.round(saleTotal - subAmount);
        perItem = Math.round(saleTotal / totalItems);
      }

      /*
       * Update the Prestige theme's price elements.
       * Prestige uses <sale-price> and <compare-at-price> custom elements,
       * or falls back to .price .price--highlight etc.
       */
      var priceBlock = document.querySelector('.product-info__block-item .price, sale-price, .price');
      if (priceBlock) {
        /* Try Prestige custom elements first */
        var salePriceEl    = document.querySelector('sale-price .text--highlight, sale-price .price--highlight, sale-price');
        var comparePriceEl = document.querySelector('compare-at-price');

        if (salePriceEl) {
          salePriceEl.textContent = formatMoney(saleTotal, currency);
        }
        if (comparePriceEl && discount > 0) {
          comparePriceEl.textContent = formatMoney(rawTotal, currency);
          comparePriceEl.style.display = '';
        } else if (comparePriceEl && discount === 0 && subDiscount === 0) {
          comparePriceEl.style.display = 'none';
        }
      }
    }

    /* ── Seal Subscriptions integration ── */
    function getSubscriptionDiscount() {
      /*
       * Seal stores the selling plan in a hidden input.
       * When "Subscribe & Save" is active, the selling_plan input has a value.
       * When "One-time purchase" is active, the selling_plan is empty or "0".
       *
       * Alivia currently offers 15% off for subscriptions.
       * Adjust this value if your subscription discount changes.
       */
      var sellingPlanInput = document.querySelector(SELLING_PLAN);
      var subsRadios = document.querySelectorAll(SUBS_RADIO);
      var isSubscription = false;

      subsRadios.forEach(function (radio) {
        if (radio.checked && radio.value !== 'one_time') {
          isSubscription = true;
        }
      });

      /* If no radio found, check if selling_plan has a value */
      if (!subsRadios.length && sellingPlanInput && sellingPlanInput.value && sellingPlanInput.value !== '0') {
        isSubscription = true;
      }

      return isSubscription ? 15 : 0; /* ← change 15 to match your sub discount % */
    }

    /* ── Listen for Seal subscription toggle changes ── */
    function watchSubscriptionChanges() {
      var subsRadios = document.querySelectorAll(SUBS_RADIO);
      subsRadios.forEach(function (radio) {
        radio.addEventListener('change', function () {
          var activeSwatch = container.querySelector('.' + ACTIVE_CLASS);
          if (activeSwatch) {
            updatePriceDisplay(activeSwatch, currency);
          }
        });
      });

      /* Also watch for selling_plan hidden input changes (MutationObserver) */
      var sellingPlanInput = document.querySelector(SELLING_PLAN);
      if (sellingPlanInput) {
        var observer = new MutationObserver(function () {
          var activeSwatch = container.querySelector('.' + ACTIVE_CLASS);
          if (activeSwatch) {
            updatePriceDisplay(activeSwatch, currency);
          }
        });
        observer.observe(sellingPlanInput, { attributes: true, attributeFilter: ['value'] });
      }
    }

    /* ── Bind click events ── */
    items.forEach(function (item) {
      item.addEventListener('click', function () {
        selectSwatch(item);
      });

      /* Keyboard accessibility */
      item.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectSwatch(item);
        }
      });
    });

    /* ── Restore last selection from localStorage ── */
    try {
      var savedQty = localStorage.getItem('qs-selected-qty');
      if (savedQty) {
        items.forEach(function (item) {
          if (item.dataset.quantity === savedQty) {
            selectSwatch(item);
          }
        });
      }
    } catch (e) { /* silent */ }

    /* ── Start watching subscription toggles ── */
    watchSubscriptionChanges();
  }

  /* ── Boot ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
